import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { Building2, User, CreditCard, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface SharedProposalHeaderProps {
  sharedBy: {
    first_name: string;
    last_name: string;
    logo_url?: string;
    logo_small_url?: string;
    agency_name?: string;
  };
  totalPrice?: number | null;
  cancellationPolicyNotes?: string | null;
  paymentEnabled?: boolean;
  onPayNow?: () => void;
  isProcessing?: boolean;
  showError?: boolean;
  errorMessage?: string;
}

const formatPrice = (price: number | null | undefined) => {
  if (!price) return '€0.00';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export const SharedProposalHeader = ({
  sharedBy,
  totalPrice,
  cancellationPolicyNotes,
  paymentEnabled = false,
  onPayNow,
  isProcessing = false,
  showError = false,
  errorMessage
}: SharedProposalHeaderProps) => {
  const { t } = useI18n();
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  console.log('[SharedProposalHeader] Props:', { 
    paymentEnabled, 
    totalPrice, 
    hasSharedBy: !!sharedBy,
    showPaymentSection: paymentEnabled && totalPrice 
  });

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
      <div className="mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo/Advisor Info */}
          <div className="flex items-center gap-4">
            {sharedBy.logo_small_url ? (
              <img
                src={sharedBy.logo_small_url}
                alt={sharedBy.agency_name || `${sharedBy.first_name} ${sharedBy.last_name}`}
                className="h-8 sm:h-10 w-8 sm:w-10 object-contain"
              />
            ) : (
              <div className="h-8 sm:h-10 w-8 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center">
                {sharedBy.agency_name ? (
                  <Building2 className="h-6 w-6 text-primary" />
                ) : (
                  <User className="h-6 w-6 text-primary" />
                )}
              </div>
            )}
            
            <div>
              <p className="text-xs sm:text-base text-gray-500">
                {t('sharedProposal.sharedBy')}
              </p>
              <p className="text-xs sm:text-base font-semibold text-gray-900">
                {`${sharedBy.first_name} ${sharedBy.last_name}`}
              </p>
            </div>
          </div>

          {/* Payment Section (right side, compact) */}
          {totalPrice && (
            <div className="flex items-center gap-4">
              {showError && (
                <Alert variant="destructive" className="py-1 px-3 flex items-center border-none">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <AlertDescription className="text-xs">
                      {errorMessage === 'stripe_not_configured' 
                        ? t('sharedProposal.paymentNotAvailable')
                        : t('sharedProposal.paymentError')}
                    </AlertDescription>
                  </div>
                </Alert>
              )}
              
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {t('sharedProposal.totalPrice')}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <div className="text-base sm:text-lg font-bold text-gray-900">
                      {formatPrice(totalPrice)}
                    </div>
                    {cancellationPolicyNotes && (
                      <button
                        onClick={() => setShowPolicyModal(true)}
                        className="flex-shrink-0"
                        aria-label={t('sharedProposal.viewCancellationPolicy')}
                      >
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                      </button>
                    )}
                  </div>
                </div>

                {paymentEnabled && (
                  <Button
                    onClick={onPayNow}
                    disabled={isProcessing}
                    size="sm"
                    className="gap-2"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span>
                      {isProcessing 
                        ? t('sharedProposal.processingPayment') 
                        : t('sharedProposal.payNow')}
                    </span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Policy Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sharedProposal.cancellationPolicy')}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">
            {cancellationPolicyNotes}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
